/**
 * Typed fetchers for /api/v1/users.
 *
 * Types come from the OpenAPI-generated schema. Regenerate via
 * `pnpm gen:api` after a backend Pydantic change.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type UserOut = components["schemas"]["UserOut"];
export type UserListOut = components["schemas"]["UserListOut"];
export type RoleAssignmentOut = components["schemas"]["RoleAssignmentOut"];
export type ProvisionUserInput = components["schemas"]["ProvisionUserIn"];

export function listUsers(
  token: string,
  opts: { limit?: number; cursor?: string | null; status?: string | null } = {},
): Promise<UserListOut> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.status) params.set("status", opts.status);
  const qs = params.toString();
  return apiFetch<UserListOut>(`/api/v1/users${qs ? `?${qs}` : ""}`, { token });
}

export function getUser(token: string, userId: string): Promise<UserOut> {
  return apiFetch<UserOut>(`/api/v1/users/${encodeURIComponent(userId)}`, { token });
}

export function provisionUser(token: string, input: ProvisionUserInput): Promise<UserOut> {
  return apiFetch<UserOut>("/api/v1/users", { method: "POST", token, json: input });
}

/**
 * Hand-written until the OpenAPI regen lands — schema source of truth
 * is `backend/src/daquma/api/v1/users.py::EntraUserSearchHit`.
 */
export interface EntraUserSearchHit {
  entra_object_id: string;
  display_name: string;
  email: string | null;
  user_principal_name: string;
}

export interface DirectorySearchOut {
  items: EntraUserSearchHit[];
}

/**
 * Searches the tenant's Entra directory for candidates to pre-provision.
 * Backed by GET /api/v1/users/directory-search; gated by `user.provision`.
 *
 * Errors the SPA should treat as "directory unavailable, paste the
 * entra_object_id manually" rather than blocking the form:
 *   - 503 `graph_permission_denied` — Graph perm not consented yet.
 *   - 503 `graph_error` — any other Graph failure (network, throttling).
 */
export function searchDirectory(
  token: string,
  query: string,
  limit = 10,
): Promise<DirectorySearchOut> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<DirectorySearchOut>(`/api/v1/users/directory-search?${params.toString()}`, {
    token,
  });
}

export function deactivateUser(token: string, userId: string, reason: string): Promise<UserOut> {
  return apiFetch<UserOut>(`/api/v1/users/${encodeURIComponent(userId)}/deactivate`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function reactivateUser(token: string, userId: string, reason: string): Promise<UserOut> {
  return apiFetch<UserOut>(`/api/v1/users/${encodeURIComponent(userId)}/reactivate`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function offboardUser(token: string, userId: string, reason: string): Promise<UserOut> {
  // "Permanent departure" — deactivates the user AND stamps
  // pending_erasure_at_utc so the Tier 3 KEK-destruction runner will
  // eventually crypto-shred the subject's PII (ADR-0002).
  return apiFetch<UserOut>(`/api/v1/users/${encodeURIComponent(userId)}/offboard`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function assignRole(
  token: string,
  userId: string,
  role: string,
  reason: string,
): Promise<RoleAssignmentOut> {
  return apiFetch<RoleAssignmentOut>(`/api/v1/users/${encodeURIComponent(userId)}/roles`, {
    method: "POST",
    token,
    json: { role, reason },
  });
}

export function revokeRole(
  token: string,
  userId: string,
  role: string,
  reason: string,
): Promise<void> {
  return apiFetch<void>(
    `/api/v1/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}/revoke`,
    { method: "POST", token, json: { reason } },
  );
}

/**
 * Per-subject DSAR export (admin-only). The JSON is downloaded by the
 * data subject — the SPA only needs counts, so the shape is left
 * intentionally unconstrained. Backend schema additions must not
 * require a SPA rebuild. The export endpoint is the one Pydantic
 * model that genuinely returns `dict[str, object]` — there is no
 * generated schema type to import.
 */
export type UserExport = Record<string, unknown>;

export function exportUser(token: string, userId: string): Promise<UserExport> {
  return apiFetch<UserExport>(`/api/v1/users/${encodeURIComponent(userId)}/export`, { token });
}
